/**
 * `/audits/:id/evidence` — the evidence catalog (issue #4).
 *
 * A read-only catalog of what has been proven in this audit: every item that carries at
 * least one evidence record or an attestation (ADR 0008), with the evidence itself and the
 * totals banner — "N verified, M of them attested" is the sentence this screen exists to
 * make possible. Items with neither are not shown; this is not a second workspace.
 */

import type { CSSProperties } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';

import { Avatar, EvidenceGlyph, MarkerChip, StateChip } from '@portal/ui';
import type { EvidenceCatalogRow } from '@portal/contracts';

import { ApiClientError, api } from '../api.js';

const LABEL: CSSProperties = {
  fontSize: 'var(--fs-label)',
  fontWeight: 600,
  letterSpacing: 'var(--tracking-label)',
  textTransform: 'uppercase',
  color: 'var(--mu)',
};

const NOTE: CSSProperties = { fontSize: 11, color: 'var(--mu)', lineHeight: 1.5 };
const MONO: CSSProperties = { fontFamily: 'var(--font-mono)' };

const CARD: CSSProperties = {
  background: 'var(--sf)',
  border: '1px solid var(--bd)',
  borderRadius: 'var(--r-lg)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

const CHIP = (fg: string, bg: string, bd: string): CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  height: 20,
  padding: '0 6px',
  borderRadius: 'var(--r-md)',
  border: `1px solid ${bd}`,
  background: bg,
  color: fg,
  fontSize: 11,
  fontWeight: 600,
  whiteSpace: 'nowrap',
});

const LINK_STATUS: Record<string, { text: string; colour: string }> = {
  REACHABLE: { text: 'reachable', colour: 'var(--ok)' },
  UNREACHABLE: { text: 'unreachable', colour: 'var(--ba)' },
  UNCHECKED: { text: 'not yet checked', colour: 'var(--mu)' },
};

function EvidenceCard({ evidence }: { readonly evidence: EvidenceCatalogRow['evidence'][number] }) {
  const link = LINK_STATUS[evidence.lastCheckStatus] ?? LINK_STATUS['UNCHECKED']!;

  return (
    <div
      style={{
        border: '1px solid var(--bd)',
        borderRadius: 'var(--r-lg)',
        padding: '8px 10px',
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ ...LABEL, fontSize: 9.5 }}>{evidence.system}</span>
        <span style={{ fontSize: 12, fontWeight: 600, flex: 1 }}>{evidence.label}</span>
        {evidence.fromProbe && (
          <span style={{ ...LABEL, fontSize: 8.5, color: 'var(--ac)' }}>probe</span>
        )}
        <span style={{ color: 'var(--mu)' }}>
          <EvidenceGlyph size={12} />
        </span>
      </div>
      {evidence.url !== null && (
        <a
          href={evidence.url}
          target="_blank"
          rel="noreferrer"
          className="mono"
          style={{
            fontSize: 10.5,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {evidence.url}
        </a>
      )}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          fontSize: 10.5,
          color: 'var(--mu)',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Avatar initials={evidence.capturedBy.initials} size={16} />
          {evidence.capturedBy.displayName} ·{' '}
          {new Date(evidence.capturedAt).toLocaleDateString('en-GB')}
        </span>
        <span style={{ color: link.colour }}>{link.text}</span>
      </div>
    </div>
  );
}

function EvidenceRow({ row }: { readonly row: EvidenceCatalogRow }) {
  const attested = (row.attestation ?? '').trim() !== '';

  return (
    <div
      style={{
        ...CARD,
        padding: '11px 13px',
        gap: 8,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ ...MONO, fontSize: 11, fontWeight: 600 }}>{row.ref}</span>
        <MarkerChip obligation={row.obligation} isContextual={false} />
        <span style={{ fontSize: 12.5, flex: 1 }}>{row.statement}</span>
        {attested && <span style={CHIP('var(--wv)', 'var(--wvb)', 'var(--wv-bd)')}>Attested</span>}
        <StateChip state={row.state} size="sm" />
      </div>

      {attested && (
        <div
          style={{
            border: '1px solid var(--wv-bd)',
            background: 'var(--wvb)',
            borderRadius: 'var(--r-lg)',
            padding: '8px 10px',
          }}
        >
          <span style={{ ...LABEL, fontSize: 9.5, color: 'var(--wv)' }}>
            Attestation, not an artefact (ADR 0008)
          </span>
          <p style={{ margin: '4px 0 0', fontSize: 12, lineHeight: 1.5 }}>{row.attestation}</p>
        </div>
      )}

      {row.evidence.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {row.evidence.map((evidence) => (
            <EvidenceCard key={evidence.id} evidence={evidence} />
          ))}
        </div>
      )}
    </div>
  );
}

export function AuditEvidenceRoute() {
  const { id = '' } = useParams<{ id: string }>();

  const catalog = useQuery({
    queryKey: ['audit-evidence', id],
    queryFn: () => api.auditEvidence(id),
  });

  if (catalog.isPending) {
    return <div style={{ padding: 24, ...NOTE }}>Loading the evidence catalog…</div>;
  }

  if (catalog.isError) {
    return (
      <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <strong style={{ color: 'var(--ba)' }}>
          {catalog.error instanceof ApiClientError
            ? catalog.error.message
            : 'The evidence catalog could not be loaded.'}
        </strong>
        <Link to={`/audits/${id}`} style={{ fontSize: 12.5 }}>
          Back to the workspace
        </Link>
      </div>
    );
  }

  const data = catalog.data;

  return (
    <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 13 }}>
      <header style={{ display: 'flex', alignItems: 'flex-end', gap: 14 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 7,
              fontSize: 12,
              color: 'var(--mu)',
            }}
          >
            <Link to={`/audits/${id}`}>Workspace</Link>
            <span>›</span>
            <span style={{ color: 'var(--tx)', fontWeight: 600 }}>Evidence catalog</span>
          </div>
          <h1
            style={{ margin: 0, font: '600 21px/1.1 var(--font-sans)', letterSpacing: '-0.01em' }}
          >
            Evidence catalog
          </h1>
          <span style={NOTE}>
            {data.totals.itemsWithProof} of {data.totals.inScope} items in scope carry proof —{' '}
            {data.totals.evidenceCount} evidence records,{' '}
            <b style={{ color: 'var(--tx)' }}>{data.totals.attestedItems}</b> resting on an
            attestation instead of an artefact.
          </span>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
          <Link to={`/audits/${id}/matrix`} style={{ fontSize: 12.5 }}>
            Repository matrix
          </Link>
          <Link to={`/audits/${id}/review`} style={{ fontSize: 12.5 }}>
            Review &amp; submit
          </Link>
        </div>
      </header>

      {data.rows.length === 0 ? (
        <div
          style={{
            ...CARD,
            padding: '30px 22px',
            alignItems: 'center',
            gap: 8,
            textAlign: 'center',
          }}
        >
          <strong style={{ fontSize: 13 }}>Nothing has been proven yet</strong>
          <span style={{ ...NOTE, maxWidth: 380 }}>
            No item in this audit carries an evidence record or an attestation. This catalog fills
            in as items are verified with proof or a written attestation.
          </span>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
          {data.rows.map((row) => (
            <EvidenceRow key={row.ref} row={row} />
          ))}
        </div>
      )}
    </div>
  );
}
